import { useEffect, useState, useCallback } from "react";
import { kickFlush, countOfflineOrders } from "../data/offlineQueue";

// Background flusher for the offline order queue (the web OfflineWorker). Retries queued orders on
// mount, on a periodic timer, on network reconnect, and when the tab regains focus — mirroring
// Android's "user activity accelerates sync, reconnect + periodic run cover the rest". The flush is
// SINGLE-FLIGHT at the module level (kickFlush), shared with the post-enqueue kick from
// submitRegisterOrder, so overlapping triggers can never double-POST an order. The pending count
// refreshes live via the "qv-offline-queue-changed" event (enqueue/remove/flush), not just the poll.
export function useOfflineOrderQueue({ intervalMs = 20000 } = {}) {
  const [pending, setPending] = useState(0);

  const refreshCount = useCallback(async () => {
    setPending(await countOfflineOrders());
  }, []);

  const flush = useCallback(async () => {
    try {
      await kickFlush();
    } catch {
      /* ignore */
    }
    refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    let alive = true;
    refreshCount();
    flush();
    const id = setInterval(() => {
      if (alive) flush();
    }, intervalMs);
    const onOnline = () => flush();
    const onVis = () => {
      if (document.visibilityState === "visible") flush();
    };
    const onQueueChanged = () => refreshCount(); // live badge on enqueue/dequeue
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("qv-offline-queue-changed", onQueueChanged);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("qv-offline-queue-changed", onQueueChanged);
    };
  }, [flush, refreshCount, intervalMs]);

  return { pending, flush };
}
